import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import { authApi } from '../../api/authApi';
import { useAppState } from '../../reduxish/appState';

interface Props {
    open: boolean;
    currentName: string;
    onClose: () => void;
}

export default function EditNameDialog({ open, currentName, onClose }: Props) {
    const { dispatch } = useAppState();
    const [name, setName] = useState(currentName);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Display name cannot be empty.');
            return;
        }
        setSubmitting(true);
        try {
            const updated = await authApi.updateProfile(trimmed);
            dispatch({ type: 'AUTH_LOGIN_SUCCESS', user: updated });
            onClose();
        } catch (err: any) {
            setError(err?.message || 'Could not update your display name.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
            <form onSubmit={handleSubmit}>
                <DialogTitle>Edit display name</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <TextField
                        autoFocus
                        label="Display name"
                        fullWidth
                        value={name}
                        onChange={e => setName(e.target.value)}
                        slotProps={{ htmlInput: { maxLength: 255 } }}
                        disabled={submitting}
                        margin="dense"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={submitting}>
                        {submitting ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
